/**
 * Scroll-controlled video spinning logic
 */

/** Detect iOS / iPadOS (Safari & Chrome both use WebKit on iOS) */
const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const initVideoSpinning = () => {
  const containers = document.querySelectorAll('.js-video-spinning-container')

  containers.forEach((container, index) => {
    if (container.dataset.videoSpinningInitialized === 'true') return
    container.dataset.videoSpinningInitialized = 'true'

    const video = container.querySelector('video')
    const canvas = container.querySelector('canvas')

    if (video instanceof HTMLVideoElement) {
      const v = video

      // --- Canvas fallback for mobile (mix-blend-mode doesn't work on <video> on iOS) ---
      const useCanvas = canvas instanceof HTMLCanvasElement
      /** @type {CanvasRenderingContext2D | null} */
      let ctx = null

      const setupCanvas = () => {
        if (!useCanvas || !canvas) return
        if (v.videoWidth === 0 || v.videoHeight === 0) return
        canvas.width = v.videoWidth
        canvas.height = v.videoHeight
        ctx = canvas.getContext('2d')
        container.setAttribute('data-canvas-active', '')
      }

      /** Track last drawn time to skip redundant redraws */
      let lastDrawnTime = -1

      /** Paint the current video frame onto the canvas, removing white bg via alpha */
      const drawFrame = () => {
        if (!ctx || !canvas) return
        if (v.readyState < v.HAVE_CURRENT_DATA) return
        if (v.currentTime === lastDrawnTime) return
        lastDrawnTime = v.currentTime

        const w = canvas.width
        const h = canvas.height

        ctx.clearRect(0, 0, w, h)
        ctx.drawImage(v, 0, 0, w, h)

        // Make white pixels transparent (MP4 has white bg instead of alpha)
        const imageData = ctx.getImageData(0, 0, w, h)
        const buf = new Uint32Array(imageData.data.buffer)
        for (let i = 0; i < buf.length; i++) {
          const px = buf[i]
          // Little-endian: 0xAABBGGRR
          const r = px & 0xFF
          const g = (px >> 8) & 0xFF
          const b = (px >> 16) & 0xFF
          const brightness = (r + g + b) / 3

          if (brightness > 240) {
            buf[i] = 0 // near-white → fully transparent
          } else if (brightness > 200) {
            // feather zone → gradual transparency for smooth edges
            const alpha = 255 - Math.round((brightness - 200) * 255 / 40)
            buf[i] = (alpha << 24) | (px & 0x00FFFFFF)
          }
        }
        ctx.putImageData(imageData, 0, 0)
      }

      // iOS Safari ignores preload="auto" on cellular and won't download
      // any data until play() is called. We call play() to kick-start the
      // load, then pause as soon as we have enough data to seek.
      const forceLoad = () => {
        if (v.readyState >= v.HAVE_CURRENT_DATA) {
          v.pause()
          return
        }
        const playPromise = v.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              v.pause()
            })
            .catch(() => {
              // Autoplay rejected — still try to load
              v.load()
            })
        }
      }
      forceLoad()

      const rawEndPosPercent = parseFloat(container.getAttribute('data-end-position') || '1')
      const endPosPercent = Math.min(Math.max(rawEndPosPercent / 100, 0), 1)
      let targetTime = 0
      /** The smoothed time we actually apply to the video */
      let displayTime = 0
      let pendingScroll = false
      let scrubRafId = 0
      /** How quickly displayTime catches up to targetTime (0–1, higher = snappier) */
      const LERP_FACTOR = 0.15
      /** Minimum time delta to bother updating currentTime */
      const MIN_DELTA = 0.02

      let containerRect = { top: 0, height: 0 }
      let startScroll = 0
      let endScroll = 0

      const updateDimensions = () => {
        const rect = container.getBoundingClientRect()
        const scrollTop = window.scrollY
        const windowHeight = window.innerHeight
        containerRect = {
          top: rect.top + scrollTop,
          height: rect.height
        }
        // Start as soon as the section's top edge enters the viewport.
        startScroll = containerRect.top - windowHeight
        // End when the section has mostly passed through the viewport.
        endScroll = containerRect.top + containerRect.height
      }

      const updateTargetTime = () => {
        const scrollTop = window.scrollY
        const distance = endScroll - startScroll
        if (!distance || containerRect.height <= 0) return

        let progress = (scrollTop - startScroll) / distance
        progress = Math.min(Math.max(progress, 0), 1)

        if (v.duration) {
          const startTime = 0
          const endTime = endPosPercent * v.duration
          // Map progress to the range [startTime, endTime], supports reverse scrubbing.
          targetTime = startTime + (progress * (endTime - startTime))
          targetTime = Math.min(Math.max(targetTime, 0), v.duration)
        }
      }

      const tickScrub = () => {
        updateTargetTime()

        // Lerp displayTime toward targetTime for smooth visual transitions
        const delta = targetTime - displayTime
        if (Math.abs(delta) > MIN_DELTA) {
          displayTime += delta * LERP_FACTOR
          // Snap when very close to avoid endless tiny steps
          if (Math.abs(targetTime - displayTime) < MIN_DELTA) {
            displayTime = targetTime
          }
          // Only update video.currentTime when not already seeking
          if (!v.seeking) {
            v.currentTime = Math.min(Math.max(displayTime, 0), v.duration)
          }
        }

        // Draw the current frame to canvas on iOS
        if (useCanvas) drawFrame()

        scrubRafId = requestAnimationFrame(tickScrub)
      }

      const startScrubLoop = () => {
        if (scrubRafId) return
        // Initialise displayTime to wherever the video currently is
        displayTime = v.currentTime || 0
        scrubRafId = requestAnimationFrame(tickScrub)
      }

      const onScroll = () => {
        if (pendingScroll) return
        pendingScroll = true
        requestAnimationFrame(() => {
          pendingScroll = false
          startScrubLoop()
        })
      }

      let pendingResize = false
      const onResize = () => {
        if (pendingResize) return
        pendingResize = true
        requestAnimationFrame(() => {
          pendingResize = false
          updateDimensions()
          startScrubLoop()
        })
      }

      window.addEventListener('resize', onResize, { passive: true })
      window.addEventListener('scroll', onScroll, { passive: true })

      const intersectionObserver = new IntersectionObserver(
        () => {
          startScrubLoop()
        },
        { rootMargin: '200px 0px', threshold: 0 }
      )

      intersectionObserver.observe(container)

      v.addEventListener('loadedmetadata', () => {
        setupCanvas()
        updateDimensions()
        startScrubLoop()
      })

      // Fallback: loadeddata fires after first frame is available
      v.addEventListener('loadeddata', () => {
        setupCanvas()
      })

      v.addEventListener('durationchange', () => {
        onResize()
      })

      window.addEventListener('load', onResize)

      updateDimensions()
      // Initial call to set position if already scrolled
      startScrubLoop()
    }
  })
}

// Initialize on DOMContentLoaded and also after any Shopify section loads (for the editor)
document.addEventListener('DOMContentLoaded', initVideoSpinning)
document.addEventListener('shopify:section:load', initVideoSpinning)

// If the script is loaded deferred, DOMContentLoaded might have already fired
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  initVideoSpinning()
}
